import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { NumberInputComponent } from '@examdojo/core/input';

export interface EditPayoutRateDialogData {
  userName: string;
  currentRate: number | null;
  latestValidFrom: string | null;
}

export interface EditPayoutRateDialogResult {
  rate: number;
  validFrom: string;
}

@Component({
  selector: 'exam-edit-payout-rate-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    ButtonComponent,
    IconComponent,
    NumberInputComponent,
  ],
  providers: [provideNativeDateAdapter()],
  template: `
    <div class="flex flex-col">
      <div class="flex items-center justify-between border-b border-gray-200 p-6 pb-4">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">Edit Payout Rate</h2>
        </div>
        <button
          type="button"
          class="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          [mat-dialog-close]="undefined"
        >
          <dojo-icon [icon]="'times'" [size]="20" />
        </button>
      </div>

      <div class="flex flex-col gap-4 p-6">
        <div class="flex flex-col gap-2">
          <y42-number-input
            [label]="'Hourly Rate'"
            [placeholder]="'5.00'"
            [formCtrl]="rateFormCtrl"
            [step]="0.01"
            [min]="0"
            [compact]="true"
          >
            <span y42InputPrefix>€</span>
            <span dojoInputSuffix>/hour</span>
          </y42-number-input>
        </div>

        <div class="custom-mat-form-wrapper">
          <div class="mat-form-label">
            <span>Effective Date</span>
          </div>
          <mat-calendar
            [selected]="effectiveDateCtrl.value"
            [minDate]="minDate"
            (selectedChange)="effectiveDateCtrl.setValue($event)"
            class="rounded-lg border border-gray-200"
          />
        </div>
      </div>

      <div class="flex justify-end gap-2 border-t border-gray-200 p-6 pt-4">
        <dojo-button [size]="'s'" [fill]="'outline'" [mat-dialog-close]="undefined"> Cancel </dojo-button>
        <dojo-button [size]="'s'" [disabled]="!rateFormCtrl.valid || !effectiveDateCtrl.valid" (click)="save()">
          <dojo-icon icon="save" class="mr-1" />
          Save
        </dojo-button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditPayoutRateDialogComponent {
  constructor() {
    this.minDate = this.calculateMinDate();
    // Auto-select the first available date, but never before today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = this.minDate > today ? this.minDate : today;
    this.effectiveDateCtrl.setValue(selectedDate);
  }

  private readonly dialogRef = inject(MatDialogRef<EditPayoutRateDialogComponent>);
  readonly data = inject<EditPayoutRateDialogData>(MAT_DIALOG_DATA);

  readonly minDate: Date;

  readonly rateFormCtrl = new FormControl<number | null>(this.data.currentRate, [
    Validators.required,
    Validators.min(0.0),
  ]);
  readonly effectiveDateCtrl = new FormControl<Date>(new Date(), {
    nonNullable: true,
    validators: [Validators.required],
  });

  save(): void {
    if (!this.rateFormCtrl.valid || !this.effectiveDateCtrl.valid || this.rateFormCtrl.value === null) {
      return;
    }

    const effectiveDate = this.effectiveDateCtrl.value;
    // Convert the date to start of day in UTC
    const validFrom = new Date(
      Date.UTC(effectiveDate.getFullYear(), effectiveDate.getMonth(), effectiveDate.getDate(), 0, 0, 0, 0),
    );

    this.dialogRef.close({
      rate: this.rateFormCtrl.value,
      validFrom: validFrom.toISOString(),
    });
  }

  private calculateMinDate(): Date {
    if (this.data.latestValidFrom) {
      const latestDate = new Date(this.data.latestValidFrom);
      // Add one day to the latest valid_from so we can't select the same date or earlier
      latestDate.setDate(latestDate.getDate() + 1);
      return latestDate;
    }
    // If no existing rates, allow from today
    return new Date();
  }
}
